import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  InputNumber,
  Switch,
  Tabs,
  Row,
  Col,
  Statistic,
  Progress,
  Tooltip,
  Divider,
  List,
  Badge,
  Avatar,
  Descriptions,
  message,
  Popconfirm,
  Alert,
  Steps,
  Radio,
  Slider,
  Upload,
  Tree,
} from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SettingOutlined,
  RobotOutlined,
  PlayCircleOutlined,
  CopyOutlined,
  ApiOutlined,
  BookOutlined,
  ReloadOutlined,
  ExportOutlined,
  ImportOutlined,
  FilterOutlined,
  ExperimentOutlined,
  FileTextOutlined,
  DownloadOutlined,
  PoweroffOutlined,
  CheckCircleOutlined,
  UploadOutlined,
} from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input;

const AgentManagement: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [knowledgeModalVisible, setKnowledgeModalVisible] = useState(false);
  const [deployModalVisible, setDeployModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [testResults, setTestResults] = useState<any[]>([]);
  const [currentStep, setCurrentStep] = useState(0);

  // 新增智能体
  const handleAddAgent = () => {
    setSelectedRecord(null);
    form.resetFields();
    setEditModalVisible(true);
  };

  // 编辑智能体
  const handleEditAgent = (record: any) => {
    setSelectedRecord(record);
    form.setFieldsValue(record);
    setEditModalVisible(true);
  };

  // 保存智能体
  const handleSaveAgent = async (values: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      if (selectedRecord) {
        message.success('智能体更新成功！');
      } else {
        message.success('智能体创建成功！');
      }
      setEditModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('保存失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 删除智能体
  const handleDeleteAgent = async (record: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      message.success(`已删除智能体：${record.agentName}`);
    } catch (error) {
      message.error('删除失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 查看智能体详情
  const handleViewAgent = (record: any) => {
    setSelectedRecord(record);
    setConfigModalVisible(true);
  };

  // 测试智能体
  const handleTestAgent = (record: any) => {
    setSelectedRecord(record);
    setTestResults([]);
    setCurrentStep(0);
    setTestModalVisible(true);
  };

  // 复制智能体
  const handleCopyAgent = async (record: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      message.success(`已复制智能体：${record.agentName}`);
    } catch (error) {
      message.error('复制失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 启用/禁用智能体
  const handleToggleStatus = async (record: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      const newStatus = record.status === 'online' ? 'offline' : 'online';
      message.success(`智能体已${newStatus === 'online' ? '启用' : '禁用'}`);
    } catch (error) {
      message.error('操作失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 部署智能体
  const handleDeployAgent = (record: any) => {
    setSelectedRecord(record);
    setDeployModalVisible(true);
  };

  // 知识库管理
  const handleKnowledgeBase = (record: any) => {
    setSelectedRecord(record);
    setKnowledgeModalVisible(true);
  };

  // 批量删除
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要删除的智能体');
      return;
    }
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      message.success(`已删除 ${selectedRowKeys.length} 个智能体`);
      setSelectedRowKeys([]);
    } catch (error) {
      message.error('批量删除失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 运行测试
  const handleRunTest = async () => {
    setLoading(true);
    try {
      const steps = [
        { step: 0, title: '初始化智能体', status: 'process' },
        { step: 1, title: '加载知识库', status: 'process' },
        { step: 2, title: '执行对话测试', status: 'process' },
        { step: 3, title: '验证响应质量', status: 'process' },
        { step: 4, title: '生成测试报告', status: 'finish' },
      ];
      
      for (let i = 0; i < steps.length; i++) {
        await new Promise(resolve => setTimeout(resolve, 800));
        setCurrentStep(i);
        setTestResults(prev => [...prev, steps[i]]);
      }
      
      message.success('智能体测试完成！');
    } catch (error) {
      message.error('测试失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 模拟智能体数据
  const agentData = [
    {
      id: 1,
      agentId: 'AGENT001',
      name: '气氛活跃Bot',
      type: '气氛活跃',
      description: '负责营造活跃氛围，调动用户情绪，提升互动参与度',
      capabilities: ['情绪调动', '互动引导', '氛围营造'],
      knowledgeBase: '活跃氛围知识库',
      status: 'online',
      priority: 8,
      maxConcurrent: 5,
      usageCount: 1256,
      successRate: 94.5,
      avgResponseTime: 1.2,
      lastActive: '2025-10-23 14:30',
      createdAt: '2025-10-20',
      tags: ['活跃', '互动', '氛围'],
      promptTemplate: '你是一个专业的气氛活跃机器人，负责营造活跃的氛围...',
    },
    {
      id: 2,
      agentId: 'AGENT002',
      name: '产品介绍Bot',
      type: '产品介绍',
      description: '专业的产品介绍和功能说明，帮助用户了解产品特性',
      capabilities: ['产品介绍', '功能说明', '特性展示'],
      knowledgeBase: '产品知识库',
      status: 'online',
      priority: 7,
      maxConcurrent: 3,
      usageCount: 856,
      successRate: 92.3,
      avgResponseTime: 1.5,
      lastActive: '2025-10-23 14:25',
      createdAt: '2025-10-20',
      tags: ['产品', '介绍', '专业'],
      promptTemplate: '你是一个专业的产品介绍机器人，负责向用户介绍产品...',
    },
    {
      id: 3,
      agentId: 'AGENT003',
      name: '客服Bot',
      type: '客服',
      description: '提供专业的客户服务，解决用户问题和咨询',
      capabilities: ['问题解答', '客户服务', '问题处理'],
      knowledgeBase: '客服知识库',
      status: 'online',
      priority: 9,
      maxConcurrent: 10,
      usageCount: 2340,
      successRate: 98.7,
      avgResponseTime: 0.8,
      lastActive: '2025-10-23 14:20',
      createdAt: '2025-10-20',
      tags: ['客服', '专业', '服务'],
      promptTemplate: '你是一个专业的客服机器人，负责解决用户问题...',
    },
    {
      id: 4,
      agentId: 'AGENT004',
      name: '情感交流Bot',
      type: '情感交流',
      description: '提供情感支持和心理安慰，与用户建立情感连接',
      capabilities: ['情感支持', '心理安慰', '情感连接'],
      knowledgeBase: '情感交流知识库',
      status: 'online',
      priority: 6,
      maxConcurrent: 2,
      usageCount: 445,
      successRate: 89.2,
      avgResponseTime: 2.1,
      lastActive: '2025-10-23 14:15',
      createdAt: '2025-10-20',
      tags: ['情感', '支持', '安慰'],
      promptTemplate: '你是一个专业的情感交流机器人，负责提供情感支持...',
    },
  ];

  const columns = [
    {
      title: '智能体信息',
      key: 'agentInfo',
      width: 200,
      render: (_: any, record: any) => (
        <Space>
          <Avatar size="large" icon={<RobotOutlined />} />
          <div>
            <div style={{ fontWeight: 'bold' }}>{record.name}</div>
            <div style={{ fontSize: 12, color: '#999' }}>{record.agentId}</div>
            <div>
              <Tag color="blue">{record.type}</Tag>
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (description: string) => (
        <Tooltip title={description}>
          <span>{description}</span>
        </Tooltip>
      ),
    },
    {
      title: '能力',
      dataIndex: 'capabilities',
      key: 'capabilities',
      render: (capabilities: string[]) => (
        <Space wrap>
          {capabilities.map((capability, index) => (
            <Tag key={index} color="green">{capability}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={status === 'online' ? 'green' : 'red'}>
          {status === 'online' ? '在线' : '离线'}
        </Tag>
      ),
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      render: (priority: number) => (
        <Badge count={priority} style={{ backgroundColor: '#52c41a' }} />
      ),
    },
    {
      title: '使用次数',
      dataIndex: 'usageCount',
      key: 'usageCount',
      width: 100,
      sorter: (a: any, b: any) => a.usageCount - b.usageCount,
    },
    {
      title: '成功率',
      dataIndex: 'successRate',
      key: 'successRate',
      width: 100,
      render: (rate: number) => (
        <Progress
          percent={rate}
          size="small"
          format={() => `${rate}%`}
          strokeColor={rate >= 90 ? '#52c41a' : rate >= 80 ? '#faad14' : '#ff4d4f'}
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_: any, record: any) => (
        <Space>
          <Tooltip title="查看详情">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleViewAgent(record)}
            >
              详情
            </Button>
          </Tooltip>
          <Tooltip title="测试智能体">
            <Button
              type="link"
              icon={<ExperimentOutlined />}
              onClick={() => handleTestAgent(record)}
            >
              测试
            </Button>
          </Tooltip>
          <Tooltip title="编辑智能体">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEditAgent(record)}
            >
              编辑
            </Button>
          </Tooltip>
          <Tooltip title="知识库">
            <Button
              type="link"
              icon={<BookOutlined />}
              onClick={() => handleKnowledgeBase(record)}
            >
              知识库
            </Button>
          </Tooltip>
          <Tooltip title={record.status === 'online' ? '禁用智能体' : '启用智能体'}>
            <Button
              type="link"
              icon={record.status === 'online' ? <PoweroffOutlined /> : <CheckCircleOutlined />}
              onClick={() => handleToggleStatus(record)}
              danger={record.status === 'online'}
            >
              {record.status === 'online' ? '禁用' : '启用'}
            </Button>
          </Tooltip>
          <Popconfirm
            title="确定要删除这个智能体吗？"
            onConfirm={() => handleDeleteAgent(record)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除智能体">
              <Button
                type="link"
                icon={<DeleteOutlined />}
                danger
              >
                删除
              </Button>
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  const agentStats = [
    { title: '总智能体数', value: 14, icon: <RobotOutlined /> },
    { title: '在线智能体', value: 12, icon: <RobotOutlined /> },
    { title: '今日调用', value: 3240, icon: <RobotOutlined /> },
    { title: '平均成功率', value: '92.5%', icon: <RobotOutlined /> },
  ];

  const agentTypes = [
    { type: '气氛活跃', count: 2, usage: 85 },
    { type: '产品介绍', count: 2, usage: 72 },
    { type: '客服', count: 3, usage: 91 },
    { type: '情感交流', count: 2, usage: 68 },
    { type: '搞笑', count: 1, usage: 45 },
    { type: '擦边', count: 1, usage: 38 },
    { type: '安全', count: 2, usage: 89 },
    { type: '营销', count: 1, usage: 76 },
  ];

  const handleEditSubmit = (values: any) => {
    console.log('保存智能体:', values);
    setEditModalVisible(false);
  };

  return (
    <div>
      <div className="page-header">
        <h1>智能体管理</h1>
        <p>定义和扩展不同的智能体，承担各自的领域对话能力，实现智能体的全生命周期管理</p>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {agentStats.map((stat, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card>
              <div className="stats-card">
                <div style={{ fontSize: 32, color: '#1890ff', marginBottom: 8 }}>
                  {stat.icon}
                </div>
                <div className="stats-number">{stat.value}</div>
                <div className="stats-label">{stat.title}</div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]}>
        {/* 智能体列表 */}
        <Col xs={24} lg={16}>
          <Card
            title="智能体列表"
            extra={
              <Space>
                <Tooltip title="调度配置">
                  <Button icon={<SettingOutlined />} onClick={() => setConfigModalVisible(true)}>
                    调度配置
                  </Button>
                </Tooltip>
                <Tooltip title="导入智能体">
                  <Button icon={<ImportOutlined />} onClick={() => message.info('导入功能开发中...')}>
                    导入
                  </Button>
                </Tooltip>
                <Tooltip title="导出智能体">
                  <Button icon={<ExportOutlined />} onClick={() => message.info('导出功能开发中...')}>
                    导出
                  </Button>
                </Tooltip>
                <Tooltip title="创建智能体">
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleAddAgent}>
                    创建智能体
                  </Button>
                </Tooltip>
              </Space>
            }
          >
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Input
                  placeholder="搜索智能体名称或ID"
                  prefix={<SearchOutlined />}
                  style={{ width: 200 }}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
                <Select 
                  placeholder="智能体类型" 
                  style={{ width: 150 }}
                  value={filterType}
                  onChange={setFilterType}
                >
                  <Option value="all">全部类型</Option>
                  <Option value="气氛活跃">气氛活跃</Option>
                  <Option value="产品介绍">产品介绍</Option>
                  <Option value="客服">客服</Option>
                  <Option value="情感交流">情感交流</Option>
                </Select>
                <Select 
                  placeholder="状态" 
                  style={{ width: 100 }}
                  value={filterStatus}
                  onChange={setFilterStatus}
                >
                  <Option value="all">全部状态</Option>
                  <Option value="online">在线</Option>
                  <Option value="offline">离线</Option>
                </Select>
                <Tooltip title="刷新数据">
                  <Button 
                    icon={<ReloadOutlined />} 
                    onClick={() => message.info('数据已刷新')}
                  />
                </Tooltip>
                <Tooltip title="高级筛选">
                  <Button icon={<FilterOutlined />} />
                </Tooltip>
              </Space>
            </div>

            <Table
              rowSelection={rowSelection}
              columns={columns}
              dataSource={agentData}
              rowKey="id"
              scroll={{ x: 1400 }}
              pagination={{
                total: agentData.length,
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 个智能体`,
              }}
            />

            {/* 批量操作 */}
            {selectedRowKeys.length > 0 && (
              <Card style={{ marginTop: 16 }}>
                <Space>
                  <span>已选择 {selectedRowKeys.length} 个智能体</span>
                  <Tooltip title="批量启用">
                    <Button icon={<CheckCircleOutlined />} onClick={() => message.info('批量启用功能开发中...')}>
                      批量启用
                    </Button>
                  </Tooltip>
                  <Tooltip title="批量禁用">
                    <Button icon={<PoweroffOutlined />} onClick={() => message.info('批量禁用功能开发中...')}>
                      批量禁用
                    </Button>
                  </Tooltip>
                  <Popconfirm
                    title={`确定要删除选中的 ${selectedRowKeys.length} 个智能体吗？`}
                    onConfirm={handleBatchDelete}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Tooltip title="批量删除">
                      <Button icon={<DeleteOutlined />} danger>
                        批量删除
                      </Button>
                    </Tooltip>
                  </Popconfirm>
                </Space>
              </Card>
            )}
          </Card>
        </Col>

        {/* 智能体类型统计 */}
        <Col xs={24} lg={8}>
          <Card title="智能体类型统计" extra={<Button type="link">查看全部</Button>}>
            <List
              size="small"
              dataSource={agentTypes}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>{item.type}</span>
                        <Badge count={item.count} style={{ backgroundColor: '#52c41a' }} />
                      </div>
                    }
                    description={
                      <div>
                        <div style={{ marginBottom: 4 }}>数量: {item.count}</div>
                        <Progress percent={item.usage} size="small" />
                        <span style={{ fontSize: 12, color: '#666' }}>使用率: {item.usage}%</span>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>

          <Card title="智能体能力" style={{ marginTop: 16 }}>
            <Space wrap>
              <Tag color="blue">情绪调动</Tag>
              <Tag color="green">互动引导</Tag>
              <Tag color="orange">氛围营造</Tag>
              <Tag color="purple">产品介绍</Tag>
              <Tag color="cyan">功能说明</Tag>
              <Tag color="red">问题解答</Tag>
              <Tag color="gold">客户服务</Tag>
              <Tag color="lime">情感支持</Tag>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* 编辑智能体模态框 */}
      <Modal
        title={selectedRecord ? '编辑智能体' : '创建智能体'}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        width={800}
        footer={[
          <Button key="cancel" onClick={() => setEditModalVisible(false)}>
            取消
          </Button>,
          <Button key="test" icon={<PlayCircleOutlined />}>
            测试
          </Button>,
          <Button key="save" type="primary" onClick={() => form.submit()}>
            保存
          </Button>,
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleEditSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="agentId"
                label="智能体ID"
                rules={[{ required: true, message: '请输入智能体ID' }]}
              >
                <Input placeholder="如: AGENT001" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="name"
                label="智能体名称"
                rules={[{ required: true, message: '请输入智能体名称' }]}
              >
                <Input placeholder="如: 气氛活跃Bot" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="type"
                label="智能体类型"
                rules={[{ required: true, message: '请选择智能体类型' }]}
              >
                <Select placeholder="选择智能体类型">
                  <Option value="气氛活跃">气氛活跃</Option>
                  <Option value="产品介绍">产品介绍</Option>
                  <Option value="客服">客服</Option>
                  <Option value="情感交流">情感交流</Option>
                  <Option value="搞笑">搞笑</Option>
                  <Option value="擦边">擦边</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="priority"
                label="优先级"
                rules={[{ required: true, message: '请输入优先级' }]}
              >
                <InputNumber min={1} max={10} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="智能体描述"
            rules={[{ required: true, message: '请输入智能体描述' }]}
          >
            <TextArea
              rows={3}
              placeholder="描述智能体的主要功能和特点"
            />
          </Form.Item>

          <Form.Item
            name="capabilities"
            label="能力列表"
            rules={[{ required: true, message: '请选择能力' }]}
          >
            <Select
              mode="multiple"
              placeholder="选择智能体能力"
              style={{ width: '100%' }}
            >
              <Option value="情绪调动">情绪调动</Option>
              <Option value="互动引导">互动引导</Option>
              <Option value="氛围营造">氛围营造</Option>
              <Option value="产品介绍">产品介绍</Option>
              <Option value="功能说明">功能说明</Option>
              <Option value="问题解答">问题解答</Option>
              <Option value="客户服务">客户服务</Option>
              <Option value="情感支持">情感支持</Option>
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="knowledgeBase"
                label="知识库"
                rules={[{ required: true, message: '请选择知识库' }]}
              >
                <Select placeholder="选择知识库">
                  <Option value="活跃氛围知识库">活跃氛围知识库</Option>
                  <Option value="产品知识库">产品知识库</Option>
                  <Option value="客服知识库">客服知识库</Option>
                  <Option value="情感交流知识库">情感交流知识库</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="maxConcurrent"
                label="最大并发数"
                rules={[{ required: true, message: '请输入最大并发数' }]}
              >
                <InputNumber min={1} max={20} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="promptTemplate"
            label="提示词模板"
            rules={[{ required: true, message: '请输入提示词模板' }]}
          >
            <TextArea
              rows={4}
              placeholder="输入智能体的提示词模板"
            />
          </Form.Item>

          <Form.Item
            name="tags"
            label="标签"
          >
            <Select
              mode="tags"
              placeholder="输入标签"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="status"
            label="状态"
            valuePropName="checked"
          >
            <Switch checkedChildren="在线" unCheckedChildren="离线" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 调度配置模态框 */}
      <Modal
        title="智能体调度配置"
        open={configModalVisible}
        onCancel={() => setConfigModalVisible(false)}
        width={800}
        footer={[
          <Button key="cancel" onClick={() => setConfigModalVisible(false)}>
            取消
          </Button>,
          <Button key="save" type="primary">
            保存
          </Button>,
        ]}
      >
        <Tabs defaultActiveKey="1">
          <Tabs.TabPane tab="调度策略" key="1">
            <div style={{ marginBottom: 16 }}>
              <h4>智能体选择策略</h4>
              <Row gutter={16}>
                <Col span={12}>
                  <label>选择算法：</label>
                  <Select defaultValue="priority" style={{ width: 150, marginLeft: 8 }}>
                    <Option value="priority">优先级优先</Option>
                    <Option value="round_robin">轮询</Option>
                    <Option value="least_used">最少使用</Option>
                    <Option value="random">随机</Option>
                  </Select>
                </Col>
                <Col span={12}>
                  <label>负载均衡：</label>
                  <Switch defaultChecked />
                </Col>
              </Row>
            </div>
            <Divider />
            <div style={{ marginBottom: 16 }}>
              <h4>并发控制</h4>
              <Row gutter={16}>
                <Col span={12}>
                  <label>全局最大并发：</label>
                  <InputNumber min={1} max={100} defaultValue={50} style={{ width: 100, marginLeft: 8 }} />
                </Col>
                <Col span={12}>
                  <label>单智能体最大并发：</label>
                  <InputNumber min={1} max={20} defaultValue={10} style={{ width: 100, marginLeft: 8 }} />
                </Col>
              </Row>
            </div>
            <Divider />
            <div>
              <h4>故障转移</h4>
              <Row gutter={16}>
                <Col span={12}>
                  <label>自动故障转移：</label>
                  <Switch defaultChecked />
                </Col>
                <Col span={12}>
                  <label>故障检测时间：</label>
                  <InputNumber min={1} max={60} defaultValue={5} style={{ width: 100, marginLeft: 8 }} />
                  <span style={{ marginLeft: 8 }}>秒</span>
                </Col>
              </Row>
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab="性能监控" key="2">
            <div>
              <h4>性能指标</h4>
              <List
                size="small"
                dataSource={[
                  { agent: '气氛活跃Bot', responseTime: 1.2, successRate: 94.5, concurrent: 3 },
                  { agent: '产品介绍Bot', responseTime: 1.5, successRate: 92.3, concurrent: 2 },
                  { agent: '客服Bot', responseTime: 0.8, successRate: 98.7, concurrent: 8 },
                  { agent: '情感交流Bot', responseTime: 2.1, successRate: 89.2, concurrent: 1 },
                ]}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      title={item.agent}
                      description={
                        <Space>
                          <span>响应时间: {item.responseTime}s</span>
                          <span>成功率: {item.successRate}%</span>
                          <span>并发数: {item.concurrent}</span>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab="协作配置" key="3">
            <div>
              <h4>多智能体协作</h4>
              <Row gutter={16}>
                <Col span={12}>
                  <label>协作模式：</label>
                  <Select defaultValue="sequential" style={{ width: 150, marginLeft: 8 }}>
                    <Option value="sequential">顺序执行</Option>
                    <Option value="parallel">并行执行</Option>
                    <Option value="conditional">条件执行</Option>
                  </Select>
                </Col>
                <Col span={12}>
                  <label>上下文传递：</label>
                  <Switch defaultChecked />
                </Col>
              </Row>
              <Divider />
              <div>
                <h4>协作规则</h4>
                <List
                  size="small"
                  dataSource={[
                    { rule: '客服Bot优先处理问题', condition: '用户问题类型=客服' },
                    { rule: '气氛活跃Bot处理冷场', condition: '对话间隔>5分钟' },
                    { rule: '情感交流Bot处理情绪', condition: '用户情绪=负面' },
                  ]}
                  renderItem={(item) => (
                    <List.Item>
                      <List.Item.Meta
                        title={item.rule}
                        description={item.condition}
                      />
                    </List.Item>
                  )}
                />
              </div>
            </div>
          </Tabs.TabPane>
        </Tabs>
      </Modal>

      {/* 智能体测试模态框 */}
      <Modal
        title="智能体测试"
        open={testModalVisible}
        onCancel={() => setTestModalVisible(false)}
        width={900}
        footer={[
          <Button key="close" onClick={() => setTestModalVisible(false)}>
            关闭
          </Button>,
          <Button 
            key="test" 
            type="primary" 
            icon={<ExperimentOutlined />}
            onClick={handleRunTest}
            loading={loading}
          >
            运行测试
          </Button>
        ]}
      >
        {selectedRecord && (
          <div>
            <Alert
              message="智能体测试"
              description={`正在测试智能体：${selectedRecord.agentName} (${selectedRecord.agentType})`}
              type="info"
              style={{ marginBottom: 16 }}
            />
            
            <Steps
              current={currentStep}
              items={[
                { title: '初始化智能体' },
                { title: '加载知识库' },
                { title: '执行对话测试' },
                { title: '验证响应质量' },
                { title: '生成测试报告' },
              ]}
              style={{ marginBottom: 24 }}
            />
            
            {testResults.length > 0 && (
              <div>
                <h4>测试结果：</h4>
                <List
                  dataSource={testResults}
                  renderItem={(item) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                        title={item.title}
                        description={`步骤 ${item.step + 1} - 执行成功`}
                      />
                      <Tag color={item.status === 'finish' ? 'green' : 'blue'}>
                        {item.status === 'finish' ? '完成' : '进行中'}
                      </Tag>
                    </List.Item>
                  )}
                />
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 知识库管理模态框 */}
      <Modal
        title="知识库管理"
        open={knowledgeModalVisible}
        onCancel={() => setKnowledgeModalVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setKnowledgeModalVisible(false)}>
            关闭
          </Button>,
          <Button key="save" type="primary">
            保存更改
          </Button>
        ]}
      >
        {selectedRecord && (
          <div>
            <Tabs defaultActiveKey="1">
              <Tabs.TabPane tab="知识文档" key="1">
                <div>
                  <Space style={{ marginBottom: 16 }}>
                    <Button icon={<PlusOutlined />} type="primary">
                      添加文档
                    </Button>
                    <Upload>
                      <Button icon={<UploadOutlined />}>上传文档</Button>
                    </Upload>
                  </Space>
                  <List
                    dataSource={[
                      { id: 1, title: '产品手册.pdf', size: '2.5MB', status: '已索引' },
                      { id: 2, title: '常见问题.docx', size: '1.2MB', status: '已索引' },
                      { id: 3, title: '用户指南.md', size: '800KB', status: '索引中' },
                    ]}
                    renderItem={(item) => (
                      <List.Item
                        actions={[
                          <Button type="link">查看</Button>,
                          <Button type="link">编辑</Button>,
                          <Button type="link" danger>删除</Button>,
                        ]}
                      >
                        <List.Item.Meta
                          avatar={<FileTextOutlined style={{ fontSize: 24 }} />}
                          title={item.title}
                          description={`大小: ${item.size} | 状态: ${item.status}`}
                        />
                      </List.Item>
                    )}
                  />
                </div>
              </Tabs.TabPane>
              
              <Tabs.TabPane tab="知识库配置" key="2">
                <Form layout="vertical">
                  <Form.Item label="向量化模型">
                    <Select defaultValue="text-embedding-ada-002">
                      <Option value="text-embedding-ada-002">OpenAI Ada-002</Option>
                      <Option value="text-embedding-3-small">OpenAI Embedding-3-Small</Option>
                      <Option value="bge-large">BGE-Large</Option>
                    </Select>
                  </Form.Item>
                  
                  <Form.Item label="相似度阈值">
                    <Slider 
                      defaultValue={0.7} 
                      min={0} 
                      max={1} 
                      step={0.05}
                      marks={{
                        0: '0',
                        0.5: '0.5',
                        1: '1.0'
                      }}
                    />
                  </Form.Item>
                  
                  <Form.Item label="最大检索数量">
                    <InputNumber defaultValue={5} min={1} max={20} />
                  </Form.Item>
                </Form>
              </Tabs.TabPane>
            </Tabs>
          </div>
        )}
      </Modal>

      {/* 部署智能体模态框 */}
      <Modal
        title="部署智能体"
        open={deployModalVisible}
        onCancel={() => setDeployModalVisible(false)}
        width={600}
        footer={[
          <Button key="close" onClick={() => setDeployModalVisible(false)}>
            取消
          </Button>,
          <Button key="deploy" type="primary" icon={<PlayCircleOutlined />}>
            开始部署
          </Button>
        ]}
      >
        {selectedRecord && (
          <div>
            <Alert
              message="部署确认"
              description={`即将部署智能体：${selectedRecord.agentName}`}
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Form layout="vertical">
              <Form.Item label="部署环境">
                <Radio.Group defaultValue="production">
                  <Radio value="development">开发环境</Radio>
                  <Radio value="staging">测试环境</Radio>
                  <Radio value="production">生产环境</Radio>
                </Radio.Group>
              </Form.Item>
              
              <Form.Item label="最大并发数">
                <InputNumber defaultValue={selectedRecord.maxConcurrent} min={1} max={100} style={{ width: '100%' }} />
              </Form.Item>
              
              <Form.Item label="超时时间(秒)">
                <InputNumber defaultValue={30} min={5} max={300} style={{ width: '100%' }} />
              </Form.Item>
              
              <Form.Item label="自动重试">
                <Switch defaultChecked />
              </Form.Item>
              
              <Form.Item label="部署说明">
                <TextArea rows={3} placeholder="输入本次部署的说明..." />
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AgentManagement;
